import React from 'react'
import { useTranslation } from 'react-i18next'
import { Grid } from '@mui/material'
import { type Nil, Text } from '@pbt/pbt-ui-components'

import { ClinicBrief } from '~/components/dashboard/timetable/scheduler/appointment/ClinicBrief'
import type { PreviousMedicalRecordConsent } from '~/components/dashboard/timetable/scheduler/appointment/types/clinicTypes'
import { getConsentTexts } from '~/components/dashboard/timetable/scheduler/appointment/utils/pmrConsentUtils'
import Typography from '~/components/elements/Typography/Typography'

import styles from './TimelinePreviousMedicalRecords.module.scss'

interface TimelinePreviousMedicalRecordsProps {
  previousMedicalRecordConsent?: PreviousMedicalRecordConsent | Nil
}

const TimelinePreviousMedicalRecords: React.FC<
  TimelinePreviousMedicalRecordsProps
> = ({ previousMedicalRecordConsent }) => {
  const { t } = useTranslation('Common')

  if (!previousMedicalRecordConsent) {
    return (
      <Grid container className={styles.root}>
        <Grid container direction="column" m={2} width="auto">
          <Grid item mb={1}>
            <Typography.H3 className={styles.titleText}>
              {t('PMR_CONSENT.PREVIOUS_MEDICAL_RECORDS_TITLE')}
            </Typography.H3>
          </Grid>
          <Grid item>
            <Typography.Paragraph>
              {t('PMR_CONSENT.PREVIOUS_PRACTICE_UNKNOWN')}
            </Typography.Paragraph>
          </Grid>
        </Grid>
      </Grid>
    )
  }

  const { consent, previousClinicContacts } = previousMedicalRecordConsent

  const { consentText, descriptionText, showConsentLine } = getConsentTexts(
    consent,
    t,
    t('Common:PMR_CONSENT.NO_PREVIOUS_PRACTICE_ADDED'),
  )

  const showClinics = consent !== null && previousClinicContacts?.length > 0

  return (
    <Grid container className={styles.root}>
      <Grid container direction="column" m={2} width="auto">
        <Grid item mb={1}>
          <Text className={styles.titleText} variant="h3">
            {t('PMR_CONSENT.PREVIOUS_MEDICAL_RECORDS_TITLE')}
          </Text>
        </Grid>
        <Grid container item gap={1}>
          <Grid item>
            {showConsentLine && (
              <Typography.H3
                className={
                  consent === false
                    ? styles.consentTextError
                    : styles.consentText
                }
              >
                {consentText}
              </Typography.H3>
            )}
            <Typography.Paragraph>{descriptionText}</Typography.Paragraph>
          </Grid>
          {showClinics && (
            <Grid
              container
              item
              columnSpacing={4}
              direction="row"
              rowSpacing={2}
            >
              {previousClinicContacts.map((clinic) => (
                <Grid item key={clinic.id}>
                  <ClinicBrief clinic={clinic} />
                </Grid>
              ))}
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  )
}

export default TimelinePreviousMedicalRecords
